/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Slider from 'components/Slider/Slider';
import Exit from 'components/Icons/Exit/Exit';
import Arrow from 'components/Icons/Arrow/Arrow';
import nextTick from 'utils/nextTick';
import Slide from './Slide/Slide';
/**
 * This is the Gallery component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Gallery({
  close,
  prev,
  next,
  images,
  current,
  onBeforeChange,
  onAfterChange,
  onExit,
}) {
  const gallery = useRef(null);
  const slider = useRef(null);
  const [size, setSize] = useState({});

  useLayoutEffect(() => {
    function onResize() {
      if (gallery.current && gallery.current.offsetWidth > 0) {
        setSize({
          width: gallery.current.offsetWidth,
          height: gallery.current.offsetHeight,
        });
      }
    }
    window.addEventListener('resize', onResize);
    nextTick(onResize);

    return function cleanup() {
      window.removeEventListener('resize', onResize);
    };
  }, [gallery]);

  useLayoutEffect(() => {
    if (current && slider.current) {
      slider.current.goto(current, true);
    }
  }, [current]);

  function renderNavigation() {
    if (images.length <= 1) {
      return null;
    }
    return (
      <nav>
        <button
          className="prev"
          onClick={() => {
            slider.current.prev();
          }}
        >
          <Arrow left title={prev} />
        </button>
        <button
          className="next"
          onClick={() => {
            slider.current.next();
          }}
        >
          <Arrow right title={next} />
        </button>
      </nav>
    );
  }

  return (
    <div ref={gallery} className="full-screen-gallery">
      <button className="exit" onClick={onExit}>
        <Exit title={close} />
      </button>
      {renderNavigation()}
      <Slider
        ref={slider}
        show={1}
        slide={1}
        onBeforeChange={onBeforeChange}
        onAfterChange={onAfterChange}
      >
        {images.map(({ id, ...props }) => (
          <Slide key={id} {...props} containerSize={size} />
        ))}
      </Slider>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Gallery.propTypes = {
  close: PropTypes.string,
  prev: PropTypes.string,
  next: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  current: PropTypes.number.isRequired,
  onExit: PropTypes.func.isRequired,
  onBeforeChange: PropTypes.func.isRequired,
  onAfterChange: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Gallery.defaultProps = {
  close: 'Close',
  prev: 'Previous',
  next: 'Next',
  images: [],
  current: 0,
  onExit: () => {},
  onBeforeChange: () => {},
  onAfterChange: () => {},
};

export default Gallery;
